import { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/HorizontalTabs.css";

// --- Import your API helpers ---
import { getApiBase, withBase } from "../config/api"; // Make sure this path is correct
import axios from "axios";
import productDataFallback from "./ProductsData";

// --- Import a fallback image (update this path if needed) ---
import fallbackImage from "../assets/fallback.jpg";

const resolveImageSrc = (src) => {
  if (!src) return null;
  if (/^https?:\/\//i.test(src)) return src;
  if (src.startsWith("/uploads") || src.startsWith("/api/uploads")) {
    return withBase(src);
  }
  return src;
};

const buildFallbackTabData = () =>
  productDataFallback.map((category) => ({
    key: category.key,
    title: category.title,
    viewMoreLink: "/products",
    collections: category.subcollections.map((collection) => ({
      key: collection.key,
      title: collection.title,
      coverImageSrc: collection.images[0]?.src || null,
    })),
  }));

const hasApiCollections = (apiData = []) =>
  Array.isArray(apiData) &&
  apiData.some(
    (category) =>
      Array.isArray(category?.subcollections) &&
      category.subcollections.some((collection) => Array.isArray(collection?.images) && collection.images.length > 0)
  );

const HorizontalTabs = () => {
  const [tabData, setTabData] = useState([]); // Will hold data from API
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null); // Set to null initially

  // --- Fetch data from your database ---
  useEffect(() => {
    const fetchProductData = async () => {
      try {
        const res = await axios.get(`${getApiBase()}/api/products-all`);
        const apiData = res.data; // This is [{ key: "saree", subcollections: [...] }, ...]

        if (!hasApiCollections(apiData)) {
          const fallbackTabs = buildFallbackTabData();
          setTabData(fallbackTabs);
          setActiveTab(fallbackTabs[0]?.key || null);
          return;
        }

        // Transform the API data to show collections
        const transformedData = apiData.map(category => {
          
          // Go through each subcollection
          const subcollectionsWithCover = category.subcollections.map(sub => {
            // Find the first image, or use null
            const coverImage = sub.images.length > 0 ? resolveImageSrc(sub.images[0].src) : null;
            return {
              key: sub.key,
              title: sub.title,
              // Use the first image as the "cover" for the slider
              coverImageSrc: coverImage, 
            };
          });
          
          return {
            key: category.key,
            title: category.title,
            viewMoreLink: "/products", // Main link for the whole category
            // The items for the slider are now the subcollections
            collections: subcollectionsWithCover 
          };
        });

        setTabData(transformedData);
        
        // Set the default active tab *after* data loads
        if (transformedData.length > 0) {
          setActiveTab(transformedData[0].key); 
        }
      } catch (err) {
        console.error("Failed to fetch product data", err);
        const fallbackTabs = buildFallbackTabData();
        setTabData(fallbackTabs);
        setActiveTab(fallbackTabs[0]?.key || null);
      } finally {
        setLoading(false);
      }
    };

    fetchProductData();
  }, []); // Empty array means this runs once

  // Find the currently active tab's data
  const currentTab = tabData.find((tab) => tab.key === activeTab);

  return (
    <div className="container py-5 ProductCategories">
      <div className="col-12">
        <h1 className="custom-heading">Our Products</h1>
        <hr className="custom-divider" />
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs justify-content-center mb-4">
        {tabData.map((tab) => (
          <li className="nav-item" key={tab.key}>
            <button
              className={`nav-link custom-tab${activeTab === tab.key ? " active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.title}
            </button>
          </li>
        ))}
      </ul>

      {/* --- Handle Loading and Empty States --- */}
      {loading && (
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading Products...</span>
          </div>
        </div>
      )}

      {!loading && !currentTab && (
        <div className="text-center p-5">
          <p className="text-muted">No products found.</p>
        </div>
      )}

      {/* Slider */}
      {/* Only render the Swiper if we have a valid tab with collections */}
      {!loading && currentTab && currentTab.collections.length > 0 && (
        <>
          <Swiper
            modules={[Navigation, Autoplay]}
            navigation
            autoplay={{
              delay: 2500,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            slidesPerView={1.2}
            spaceBetween={16}
            breakpoints={{
              576: { slidesPerView: 2 },
              768: { slidesPerView: 3 },
              992: { slidesPerView: 4 },
            }}
          >
            {/* Loop over the COLLECTIONS, not the images */}
            {currentTab.collections.map((collection) => (
              <SwiperSlide key={collection.key}>
                <div className="product_card h-100 text-center">
                  <img
                    src={collection.coverImageSrc || fallbackImage} // Use the cover image
                    className="card-img-top"
                    alt={collection.title} // Use the collection title
                    style={{ objectFit: "cover", borderRadius: "8px" }}
                  />
                  <div className="card-body p-2">
                    {/* Use the collection title */}
                    <p className="product-name fw-semibold">{collection.title}</p> 
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* View More */}
          <div className="text-center mt-4">
            <a href={currentTab.viewMoreLink} className="btn btn-lg btn-primary px-5 py-2 Primary-Button">
              View More
            </a>
          </div>
        </>
      )}
    </div>
  );
};

export default HorizontalTabs;
